import { Badge, Button, Group, Select, Stack, Text } from '@mantine/core';
import { useState } from 'react';

import { describeFailure } from '../../shared/ui';
import { useRoleChange, useRoles } from './queries';

/**
 * Роли в карточке: то, что есть, — с отзывом; то, чего нет, — выдача с подтверждением.
 * Подтверждение — второе нажатие той же кнопки: «Выдать teacher?» → «Да, выдать». Никаких
 * модалок ради двух действий в день (AD-A7). Результат приходит из ответа API, а не из
 * оптимистичного обновления: карточка показывает то, что сервер применил.
 */
export function RolesEditor({ userId, held }: { userId: string; held: string[] }) {
  const roles = useRoles();
  const { grant, revoke } = useRoleChange(userId);
  const [pending, setPending] = useState<{ action: 'grant' | 'revoke'; slug: string } | null>(null);

  const available = (roles.data ?? []).filter((role) => !held.includes(role.slug));
  // Пустой выпадающий список без объяснения — это «что-то сломалось» на вид. Каталог из нуля
  // ролей и каталог, из которого всё уже выдано, — разные новости, и обе говорятся словами
  // (AD-A11: у экрана с запросом есть состояние пустого списка).
  const loaded = !roles.isPending && !roles.isError;
  const catalogEmpty = loaded && (roles.data ?? []).length === 0;
  const nothingToGrant = loaded && available.length === 0;
  const error = grant.error ?? revoke.error;
  const busy = grant.isPending || revoke.isPending;

  function confirm() {
    if (!pending) return;
    const { action, slug } = pending;
    setPending(null);
    (action === 'grant' ? grant : revoke).mutate(slug);
  }

  return (
    <Stack gap="xs">
      <Group gap={6}>
        {held.length === 0 ? <Text c="dimmed">Ролей нет</Text> : null}
        {held.map((slug) => (
          <Badge
            key={slug}
            variant="light"
            rightSection={
              <Button
                variant="transparent"
                size="compact-xs"
                aria-label={`Отозвать ${slug}`}
                onClick={() => setPending({ action: 'revoke', slug })}
                disabled={busy}
              >
                ×
              </Button>
            }
          >
            {slug}
          </Badge>
        ))}
      </Group>
      <Group align="flex-end" gap="xs">
        <Select
          label="Выдать роль"
          placeholder={
            roles.isError
              ? 'Каталог ролей недоступен'
              : catalogEmpty
                ? 'Каталог ролей пуст'
                : nothingToGrant
                  ? 'Все роли уже выданы'
                  : 'Роль'
          }
          data={available.map((role) => ({
            value: role.slug,
            label: `${role.displayName} (${role.slug})`,
          }))}
          value={pending?.action === 'grant' ? pending.slug : null}
          onChange={(slug) => setPending(slug ? { action: 'grant', slug } : null)}
          disabled={busy || roles.isPending || roles.isError || nothingToGrant}
          w={260}
        />
      </Group>
      {pending ? (
        <Group gap="xs">
          <Text size="sm">
            {pending.action === 'grant' ? 'Выдать' : 'Отозвать'} роль <b>{pending.slug}</b>?
          </Text>
          <Button size="xs" onClick={confirm} loading={busy}>
            Да, {pending.action === 'grant' ? 'выдать' : 'отозвать'}
          </Button>
          <Button size="xs" variant="default" onClick={() => setPending(null)} disabled={busy}>
            Отмена
          </Button>
        </Group>
      ) : null}
      {error ? (
        <Text size="sm" c="red" role="alert">
          {describeFailure(error)}
        </Text>
      ) : null}
      {roles.isError ? (
        <Text size="sm" c="red" role="alert">
          {describeFailure(roles.error)}
        </Text>
      ) : null}
    </Stack>
  );
}
